import { useEffect, useState } from 'react';
import { useV2 } from './ctx';
import { Pill } from './ui';
import { rp, shortDate } from './model';

// Pembukuan stage 1, Bayangan (Director, 4 Nov 2026): the bot's draft of each
// Accurate transaction next to what Aurel actually typed, and the score per
// rule. Read-only: nothing here writes to Accurate (server/services/bayangan.js).
const RULES = { uang_muka_farman: 'Uang muka Farman' };
const STATUS = {
  sama: ['done', 'sama persis'],
  beda: ['pend', 'beda'],
  menunggu: ['work', 'menunggu Aurel'],
  tidak_ada: ['neutral', 'tidak ada di Accurate'],
};
const BANK = { '1102-001': 'BCA', '1102-006': 'OCBC' };
const time = t => (t ? new Date(t).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : '');

export default function Bayangan() {
  const { api } = useV2();
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.bayangan().then(setR).catch(e => setR({ error: e.message }));
  useEffect(() => { load(); }, []);
  const run = async () => { setBusy(true); try { await api.runBayangan(); await load(); } catch (e) { alert(e.message); } setBusy(false); };

  if (!r) return <p className="hint">Memuat…</p>;
  if (r.error) return <p className="hint">Tidak bisa memuat: {r.error}</p>;
  const scoreLine = Object.entries(r.score).map(([rule, s]) => {
    const done = (s.sama || 0) + (s.beda || 0);
    return `${RULES[rule] || rule}: ${s.sama || 0} dari ${done} sama persis${done ? ` (${Math.round((100 * (s.sama || 0)) / done)}%)` : ''}`;
  }).join(' · ');

  return (
    <div>
      <div className="row" style={{ padding: '6px 0 10px' }}>
        <div className="grow" style={{ whiteSpace: 'normal' }}>
          <div className="m" style={{ whiteSpace: 'normal' }}>
            Bot menyiapkan transaksi Accurate dari email bank + Buku Kas, lalu dibandingkan dengan yang diketik Aurel. Tidak ada yang dikirim ke Accurate.
          </div>
          <div className="t" style={{ marginTop: 6, whiteSpace: 'normal' }}>{scoreLine || 'Belum ada draf'}</div>
          {r.since && <div className="m">Email bank sejak {shortDate(r.since)}</div>}
        </div>
        <button type="button" className="btn ghost small" disabled={busy} onClick={run}>{busy ? 'Mengecek…' : 'Cek sekarang'}</button>
      </div>

      <div className="group" style={{ margin: 0 }}>
        {r.drafts.map(d => {
          const [kind, label] = STATUS[d.status] || ['neutral', d.status];
          const isOpen = open === d.id;
          return (
            <div key={d.id}>
              <div className="row tap" onClick={() => setOpen(isOpen ? null : d.id)}>
                <div className="grow">
                  <div className="t">{shortDate(d.tanggal)} · {RULES[d.rule] || d.rule} · {BANK[d.bank_no] || d.bank_no}</div>
                  <div className="m">{d.transfers.map(t => t.remark || '—').join(' · ')}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {d.confidence === 'tebakan' && <Pill kind="neutral">tebakan</Pill>}
                  <Pill kind={kind}>{label}</Pill>
                  <span className="amt">{rp(d.amount)}</span>
                  <span className="chev">{isOpen ? '⌄' : '›'}</span>
                </div>
              </div>
              {isOpen && <Detail d={d} />}
            </div>
          );
        })}
        {!r.drafts.length && <div className="row"><div className="m">Belum ada transfer ke Pak Farman sejak email bank masuk.</div></div>}
      </div>

      {r.tanpaDraf.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="m" style={{ whiteSpace: 'normal', marginBottom: 4 }}>Dicatat Aurel tanpa email bank (bot tidak bisa membuat draf):</div>
          <div className="group" style={{ margin: 0 }}>
            {r.tanpaDraf.map(p => (
              <div className="row" key={p.id}><div className="grow"><div className="t">{shortDate(p.trans_date)} · {p.number}</div><div className="m">{p.description}</div></div><span className="amt">{rp(p.amount)}</span></div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Lines({ title, lines, flagged }) {
  const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  return (
    <div style={{ minWidth: 0 }}>
      <div className="m" style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12.5, padding: '3px 0', background: flagged(l) ? 'var(--amber-dim)' : 'none', borderRadius: 4 }}>
          <span style={{ fontFamily: 'var(--mono)', width: 64, flex: 'none' }}>{l.account_no || '?'}</span>
          <span className="grow" style={{ whiteSpace: 'normal' }}>{l.account_name || ''}{l.why ? <span className="muted"> · {l.why}</span> : null}</span>
          <span className="amt">{rp(l.amount)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4 }}><span className="amt" style={{ fontWeight: 600 }}>{rp(total)}</span></div>
    </div>
  );
}

function Detail({ d }) {
  const key = l => `${l.account_no}|${Math.round(l.amount)}`;
  const botOnly = new Set((d.diff?.bot_saja || []).map(key));
  const aurelOnly = new Set((d.diff?.aurel_saja || []).map(key));
  return (
    <div style={{ padding: '6px 12px 14px', background: 'var(--surface2)' }}>
      <div className="m" style={{ whiteSpace: 'normal', marginBottom: 8 }}>
        {d.transfers.map(t => `${t.bank} ${time(t.at)} · Rp ${rp(t.amount)} · "${t.remark || ''}"`).join('  |  ')}
      </div>
      {d.note && <div className="m" style={{ whiteSpace: 'normal', marginBottom: 8, color: 'var(--pend)' }}>{d.note}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <Lines title="Draf bot" lines={d.lines} flagged={l => botOnly.has(key(l))} />
        {d.aurel_lines.length
          ? <Lines title={`Aurel · ${(d.txn_numbers || []).join(', ')}`} lines={d.aurel_lines} flagged={l => aurelOnly.has(key(l))} />
          : <div className="m" style={{ whiteSpace: 'normal' }}>Aurel belum mencatat transaksi ini di Accurate.</div>}
      </div>
    </div>
  );
}
